import React from 'react';
import { Flex, Text, Box } from '@backstage/ui';
import { Entity } from '@backstage/catalog-model';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { appTranslationRef } from '../../../../core/i18n';
import styles from './entity-inspector.module.css';

interface InspectorOverviewProps {
  entity: Entity;
}

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Box className={styles.section}>
    <Text
      variant="body-x-small"
      color="secondary"
      weight="bold"
      className={styles.sectionTitle}
    >
      {title}
    </Text>
    {children}
  </Box>
);

const MetaRow = ({ label, value }: { label: string; value: string }) => (
  <Flex justify="between" className={styles.metaRow}>
    <Text variant="body-small" color="secondary">
      {label}
    </Text>
    <Text variant="body-small" className={styles.monoText}>
      {value}
    </Text>
  </Flex>
);

const KeyValueList = ({
  data,
  emptyText,
}: {
  data: Record<string, string> | undefined;
  emptyText: string;
}) => {
  const entries = data ? Object.entries(data) : [];
  if (entries.length === 0) {
    return (
      <Text variant="body-small" color="secondary">
        {emptyText}
      </Text>
    );
  }
  return (
    <Box className={styles.kvContainer}>
      {entries.map(([key, value]) => (
        <Flex
          key={key}
          justify="between"
          gap="3"
          className={styles.kvRow}
        >
          <Text
            variant="body-x-small"
            className={styles.kvKey}
          >
            {key}
          </Text>
          <Text
            variant="body-x-small"
            className={styles.kvValue}
          >
            {value}
          </Text>
        </Flex>
      ))}
    </Box>
  );
};

export const InspectorOverview = ({ entity }: InspectorOverviewProps) => {
  const { t } = useTranslationRef(appTranslationRef);

  return (
    <Flex direction="column" gap="2">
      <Section title={t('entityInspector.metadata')}>
        <MetaRow label={t('entityInspector.name')} value={entity.metadata.name} />
        <MetaRow
          label={t('entityInspector.namespace')}
          value={entity.metadata.namespace ?? 'default'}
        />
        <MetaRow label={t('entityInspector.kind')} value={entity.kind} />
      </Section>

      <Section title={t('entityInspector.labels')}>
        <KeyValueList
          data={entity.metadata.labels}
          emptyText={t('entityInspector.noLabels')}
        />
      </Section>

      <Section title={t('entityInspector.annotations')}>
        <KeyValueList
          data={entity.metadata.annotations}
          emptyText={t('entityInspector.noAnnotations')}
        />
      </Section>

      <Section title={t('entityInspector.relations')}>
        {entity.relations && entity.relations.length > 0 ? (
          <Box className={styles.kvContainer}>
            {entity.relations.map((rel, i) => (
              <Flex
                key={i}
                gap="3"
                className={styles.kvRow}
              >
                <Text
                  variant="body-x-small"
                  color="secondary"
                  className={styles.relationType}
                >
                  {rel.type}
                </Text>
                <Text
                  variant="body-x-small"
                  className={styles.relationTarget}
                >
                  {rel.targetRef}
                </Text>
              </Flex>
            ))}
          </Box>
        ) : (
          <Text variant="body-small" color="secondary">
            {t('entityInspector.noRelations')}
          </Text>
        )}
      </Section>
    </Flex>
  );
};
